import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  TrendingUp,
  Video,
  BookOpen,
  FileText,
  CalendarRange,
  ChevronRight,
  ShieldCheck,
  GraduationCap,
  ArrowRight,
  Sparkles,
  Activity,
  CheckCircle2,
  BarChart3,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Footer } from "@/components/Footer";
import { Icon3D } from "@/components/Icon3D";
import { HomepagePreviewSection } from "@/components/HomepagePreviewSection";
import { SeoHead, SEO_PRESETS } from "@/components/SeoHead";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const ICON_MAP = {
  shield: ShieldCheck,
  graduation: GraduationCap,
  sparkles: Sparkles,
  video: Video,
  book: BookOpen,
  file: FileText,
  calendar: CalendarRange,
  trending: TrendingUp,
};

const ICON3D_MAP = {
  shield: "shield-check",
  graduation: "course",
  sparkles: "sparkles",
  video: "video",
  book: "book",
  file: "document",
  calendar: "calendar",
  trending: "trending",
};

function MiniChartSparkline() {
  return (
    <svg viewBox="0 0 200 60" className="w-full h-12" aria-hidden="true">
      <defs>
        <linearGradient id="hero-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1ec7a0" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#1ec7a0" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="hero-spark-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1ec7a0" />
          <stop offset="100%" stopColor="#6ee7c8" />
        </linearGradient>
      </defs>
      <path d="M0,48 L20,42 L40,46 L60,32 L80,36 L100,24 L120,28 L140,16 L160,18 L180,8 L200,6 L200,60 L0,60 Z" fill="url(#hero-spark-fill)" />
      <path d="M0,48 L20,42 L40,46 L60,32 L80,36 L100,24 L120,28 L140,16 L160,18 L180,8 L200,6"
            fill="none" stroke="url(#hero-spark-line)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="200" cy="6" r="3.5" fill="#6ee7c8" />
      <circle cx="200" cy="6" r="6" fill="#1ec7a0" opacity="0.3" />
    </svg>
  );
}

export default function Home() {
  const [content, setContent] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/content/homepage").then((r) => setContent(r.data)).catch(() => setContent({}));
    // Public events feed: only fetch the publicly-readable list. The /events
    // endpoint requires auth, so we use a safe try/catch and silently fall back.
    api.get("/events/public/upcoming").then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  if (!content) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const hero = content.hero || {};
  const trust = content.trust_points || [];
  const featuresSec = content.features_section || {};
  const features = content.features || [];
  const stepsSec = content.steps_section || {};
  const steps = content.steps || [];
  const finalCta = content.final_cta || {};

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      <SeoHead {...SEO_PRESETS.home} />
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="glow-orb glow-orb-teal w-[700px] h-[700px] -top-48 -right-48 anim-pulse-glow" />
        <div className="glow-orb glow-orb-navy w-[600px] h-[600px] top-[40vh] -left-48" />
        <div className="glow-orb glow-orb-gold w-[400px] h-[400px] top-[80vh] right-[20vw] opacity-20" />
      </div>

      <header className="relative z-10 glass-topbar" data-testid="home-header">
        <div className="container flex h-16 sm:h-20 items-center justify-between">
          <Logo size="md" logoUrl={content.logo_url} />
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button asChild data-testid="home-cta-login" size="default" className="px-3 sm:px-5">
              <Link to="/login">
                <span className="hidden xs:inline sm:inline">Login Portal</span>
                <span className="xs:hidden sm:hidden">Login</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative z-10 pt-12 pb-20 sm:pt-16 sm:pb-28">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <div className="container relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              {hero.badge_text && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card/60 backdrop-blur-sm anim-fade-up" data-testid="hero-badge">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal animate-glow" />
                  <span className="text-xs font-medium text-muted-foreground tracking-wider">{hero.badge_text}</span>
                </div>
              )}
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-heading font-bold tracking-tight leading-[1.05] anim-fade-up" style={{ animationDelay: "0.1s" }}>
                {hero.headline_line1}<br />
                <span className="text-gradient-teal">{hero.headline_line2}</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0 anim-fade-up" style={{ animationDelay: "0.2s" }}>
                {hero.subheadline}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 anim-fade-up" style={{ animationDelay: "0.3s" }}>
                <Button asChild size="lg" data-testid="hero-primary-cta">
                  <Link to="/login"><span>{hero.primary_cta || "Login Student Portal"}</span> <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="hero-secondary-cta">
                  <a href="#features"><span>{hero.secondary_cta || "Learn About the Class"}</span> <ChevronRight className="h-4 w-4" /></a>
                </Button>
              </div>

              {hero.metrics?.length > 0 && (
                <div className="mt-10 grid grid-cols-3 gap-3 max-w-md mx-auto lg:mx-0 anim-fade-up" style={{ animationDelay: "0.4s" }}>
                  {hero.metrics.map((m, i) => (
                    <div key={i} className="rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm px-3 py-2.5">
                      <p className="text-xl font-heading font-bold text-gradient-teal">{m.value}</p>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-5 relative anim-fade-up" style={{ animationDelay: "0.35s" }}>
              <div className="relative h-[360px] sm:h-[440px] lg:h-[520px]">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/30 via-secondary/20 to-transparent blur-3xl rounded-full" />

                <div className="floating-panel anim-float absolute top-4 left-2 right-2 sm:left-6 sm:right-6 p-5 z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="icon-tile h-8 w-8"><Activity className="h-4 w-4" /></span>
                      <div>
                        <p className="text-xs text-muted-foreground">Course Progress</p>
                        <p className="text-sm font-semibold">SC101–SC106</p>
                      </div>
                    </div>
                    <span className="status-pill bg-teal/15 text-teal border-teal/30">Active</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <p className="text-3xl font-heading font-bold text-gradient-teal">68%</p>
                    <span className="text-[11px] text-teal flex items-center gap-1"><TrendingUp className="h-3 w-3" /> +12% this month</span>
                  </div>
                  <MiniChartSparkline />
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {[
                      { label: "Completed", value: "4/6" },
                      { label: "Recording", value: "2/2" },
                      { label: "Notes", value: "3/6" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-lg bg-muted/50 px-2.5 py-2 border border-border/40">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</p>
                        <p className="text-sm font-semibold">{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="floating-panel anim-float-delayed absolute bottom-6 right-0 sm:right-2 left-12 sm:left-24 p-4 z-20">
                  <div className="flex items-center gap-3">
                    <span className="icon-tile h-9 w-9 shrink-0"><CheckCircle2 className="h-4 w-4" /></span>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Module just completed</p>
                      <p className="text-sm font-semibold truncate">SC104 — Candlesticks Pattern</p>
                    </div>
                  </div>
                </div>

                <div className="floating-panel anim-float-delayed absolute top-0 right-0 p-3 z-20" style={{ animationDelay: "2s" }}>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-teal animate-glow" />
                    <p className="text-xs font-semibold">Live Session</p>
                  </div>
                </div>

                <div className="floating-panel anim-float absolute bottom-2 left-0 p-3 z-10" style={{ animationDelay: "1s" }}>
                  <div className="flex items-center gap-2">
                    <span className="icon-tile h-8 w-8"><BarChart3 className="h-4 w-4" /></span>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Session 2</p>
                      <p className="text-xs font-semibold">Available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 sm:mt-24 grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {trust.map((p, i) => {
              const Icon = ICON_MAP[p.icon] || ShieldCheck;
              const icon3d = ICON3D_MAP[p.icon] || "shield-check";
              return (
                <Card key={i} className="glass-card glass-card-hover anim-fade-up" style={{ animationDelay: `${0.5 + i * 0.1}s` }} data-testid={`trust-card-${i}`}>
                  <CardContent className="p-6">
                    <Icon3D name={icon3d} size={56} fallback={Icon} className="mb-4" alt="" />
                    <h3 className="font-heading font-semibold text-base">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Public homepage preview videos (managed via Admin → Preview Videos) */}
      <HomepagePreviewSection />

      {events.length > 0 && (
        <section id="upcoming-events" className="relative z-10 py-16 sm:py-20" data-testid="home-events-section">
          <div className="container">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 max-w-5xl mx-auto">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">Upcoming events</p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Public webinars &amp; classes</h2>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl">
                  Free and paid public events from Maahad Bursa. Anyone can register — no portal account required.
                </p>
              </div>
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {events.slice(0, 3).map((ev, i) => {
                const isPaid = ev.category === "berbayar";
                return (
                  <Card key={ev.id || i} className="glass-card glass-card-hover anim-fade-up flex flex-col" style={{ animationDelay: `${0.1 + i * 0.1}s` }} data-testid={`home-event-card-${i}`}>
                    <CardContent className="p-6 flex flex-col flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${isPaid ? "bg-gold/10 text-gold border border-gold/30" : "bg-teal/10 text-teal border border-teal/30"}`}>
                          {isPaid ? "Paid" : "Free"}
                        </span>
                        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{ev.location_type === "online" ? "Online" : ev.location_type === "physical" ? "In Person" : "Hybrid"}</span>
                      </div>
                      <h3 className="mt-4 text-lg font-heading font-semibold leading-snug">{ev.title}</h3>
                      <p className="mt-1 text-xs text-muted-foreground flex items-center gap-2">
                        <CalendarRange className="h-3.5 w-3.5 text-primary" />
                        {ev.event_date} · {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ""}
                      </p>
                      {ev.location && (
                        <p className="mt-1 text-xs text-muted-foreground">{ev.location}</p>
                      )}
                      {ev.description && (
                        <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{ev.description}</p>
                      )}
                      <div className="mt-auto pt-4">
                        {ev.registration_link ? (
                          <Button asChild variant="outline" size="sm" className="w-full" data-testid={`home-event-cta-${i}`}>
                            <a href={ev.registration_link} target="_blank" rel="noreferrer">
                              Register Now <ArrowRight className="h-3.5 w-3.5" />
                            </a>
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="w-full" disabled>
                            Registration link coming soon
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section id="features" className="relative z-10 py-16 sm:py-24">
        <div className="container">
          <div className="max-w-2xl">
            {featuresSec.kicker && <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{featuresSec.kicker}</p>}
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{featuresSec.heading}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">{featuresSec.description}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => {
              const Icon = ICON_MAP[f.icon] || Video;
              const icon3d = ICON3D_MAP[f.icon] || "video";
              return (
                <Card key={i} className="glass-card glass-card-hover group" data-testid={`feature-card-${i}`}>
                  <CardContent className="p-6">
                    <Icon3D name={icon3d} size={64} fallback={Icon} className="mb-5" alt="" />
                    <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>Learn more</span>
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-16 sm:py-24 border-t border-border/60">
        <div className="container">
          <div className="max-w-2xl">
            {stepsSec.kicker && <p className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{stepsSec.kicker}</p>}
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{stepsSec.heading}</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {steps.map((s, i) => (
              <Card key={i} className="glass-card glass-card-hover relative overflow-hidden" data-testid={`step-card-${i}`}>
                <CardContent className="p-6">
                  <span className="text-6xl font-heading font-bold text-gradient-teal opacity-20 absolute top-2 right-5">{s.n}</span>
                  <div className="icon-tile h-9 w-9 mb-4">
                    <span className="text-xs font-bold">{s.n}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-lg">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-20 sm:py-28">
        <div className="container">
          <Card className="glass-card overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-secondary/15" />
            <div className="absolute -top-20 -right-20 glow-orb glow-orb-teal w-[400px] h-[400px] opacity-30" />
            <CardContent className="p-10 sm:p-16 relative text-center">
              {finalCta.tagline && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-background/30 backdrop-blur-sm mb-5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium tracking-wider">{finalCta.tagline}</span>
                </div>
              )}
              <h2 className="text-3xl sm:text-5xl font-heading font-bold tracking-tight">
                {finalCta.headline_part1} <span className="text-gradient-teal">{finalCta.headline_part2}</span>
              </h2>
              <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">{finalCta.description}</p>
              <Button asChild size="lg" className="mt-8" data-testid="final-cta">
                <Link to="/login"><span>{finalCta.button || "Sign In Now"}</span> <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer content={content.footer} logoUrl={content.logo_url} />
    </div>
  );
}
